import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { GitBranch, Check } from 'lucide-react';
import { useActivityStore } from '@/stores/activityStore';
import { formatDistanceToNow } from 'date-fns';

export function ActivityIndicator() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  
  const { 
    activities, 
    unreadCount, 
    markAllAsRead,
  } = useActivityStore();

  const handleActivityClick = (activity: any) => {
    // Navigate to the entity that was changed
    const routeMap: Record<string, string> = {
      sites: '/assets',
      zones: '/assets',
      systems: '/assets',
      endpoints: '/assets',
      cloud_services: '/assets',
      risks: '/risks',
      controls: '/controls',
      incidents: '/incidents',
      evidence: '/evidence',
      tasks: '/tasks',
      suppliers: '/suppliers',
    };

    const route = routeMap[activity.entity_type];
    if (route) {
      navigate(route);
      setOpen(false);
    }
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'created':
        return 'text-green-600 dark:text-green-400';
      case 'updated':
        return 'text-blue-600 dark:text-blue-400';
      case 'deleted':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-muted-foreground';
    }
  };

  const getEntityTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      sites: 'Site',
      zones: 'Zone',
      systems: 'System',
      endpoints: 'Endpoint',
      cloud_services: 'Cloud Service',
      cloud_resources: 'Cloud Resource',
      risks: 'Risk',
      controls: 'Control',
      incidents: 'Incident',
      evidence: 'Evidence',
      tasks: 'Task',
      suppliers: 'Supplier',
      internet_connections: 'Internet Connection',
      dmz_configs: 'DMZ Configuration',
      firewall_rules: 'Firewall Rule',
      org_layout: 'System Layout',
      pending_owner_assignments: 'Owner Assignment',
      wazuh_configs: 'Security Config',
      discovered_identities: 'Discovery',
      conduits: 'Conduit',
    };
    return labels[type] || type;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon"
          className="relative"
        >
          <GitBranch className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              variant="danger" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-[10px]"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold text-sm">Recent Activity</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllAsRead}
              className="h-7 text-xs"
            >
              <Check className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="h-[400px]">
          {activities.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <GitBranch className="h-12 w-12 text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground">No recent activity</p>
            </div>
          ) : (
            <div className="divide-y">
              {activities.map((activity) => {
                const userName = activity.user_email.split('@')[0]; // Use email prefix
                const timeAgo = formatDistanceToNow(new Date(activity.created_at), { addSuffix: true });
                
                return (
                  <button
                    key={activity.id}
                    onClick={() => handleActivityClick(activity)}
                    className="w-full px-4 py-3 text-left hover:bg-accent transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm">
                          <span className={getActionColor(activity.action)}>
                            {getEntityTypeLabel(activity.entity_type)} {activity.action}
                          </span>
                          {' · '}
                          <span className="text-muted-foreground">
                            {timeAgo.replace(' ago', '')}
                          </span>
                          {' · '}
                          <span className="font-medium">
                            {userName}
                          </span>
                        </p>
                        {activity.entity_name && (
                          <p className="text-sm text-foreground mt-1 truncate">
                            {activity.entity_name}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
